import { isEqual } from 'lodash';
import { IntlShape } from 'react-intl';
import { getEvent } from '../../commands/sportsCommands';
import { AUTO_ACCEPT_ODDS_CHANGE, SHOW_TOTAL_STAKES_TOGGLE } from '../../data-types/betting/bettingEnums';
import {
  OutcomeSelectionByEventChanged,
  PendingBet,
  PendingBetParams,
  SystemBetConfigSettings,
  SystemBetStakeDetails,
  SystemBetTypeExtended,
} from '../../data-types/betting/bettingInterfaces';
import { SNACKBAR_SEVERITY } from '../../data-types/enums';
import { StringKeyBooleanValuePair } from '../../data-types/interfaces';
import { MAX_BETS_IN_ACCA } from '../../data-types/sports/sportsConstants';
import { EventItem, TrendingBet } from '../../data-types/sports/sportsInterfaces';
import { SuperchargedBetInfo } from '../../data-types/supercharged-bet/superchargedBetInterfaces';
import { filterOutPlacedBets } from '../../utils/bet';
import { getOfferState } from '../../utils/betslip';
import { enqueueSnackbarPrompt } from '../../utils/prompts';
import { store } from '../store';
import {
  APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY,
  DEQUEUE_SUPERCHARGED_BET,
  ENQUEUE_SUPERCHARGED_BET,
  REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS,
  REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  REMOVE_SYSTEM_BET_STAKE_DETAILS,
  REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS,
  SET_ALL_STAKES_STATUS,
  SET_AUTO_ACCEPT_ODDS_CHANGE,
  SET_AVAILABLE_SYSTEM_BET_TYPES,
  SET_BETSLIP_SNACKBAR,
  SET_IS_PLACING_BET,
  SET_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  SET_PENDING_BETS,
  SET_SUPERCHARGED_BETS,
  SET_SYSTEM_BET_CONFIG,
  SET_SYSTEM_BET_PLACING_ORDER,
  SET_SYSTEM_BET_STAKE_DETAILS,
  SET_SYSTEM_BET_STAKE_VALIDATION_STATUS,
  SET_TRENDING_BETS,
  SET_TRENDING_BETS_CAROUSEL_OPEN,
} from '../types/betTypes';
import { SET_PENDING_TICKET_STATUSES } from '../types/ticketTypes';

export const removeAllPlacedBets = () => {
  const {
    bets: { pendingBets },
    tickets: { pendingTickets },
  } = store.getState();

  const sanitizedPendingBets = filterOutPlacedBets(pendingBets, pendingTickets);

  setPendingBets(sanitizedPendingBets);

  store.dispatch({
    type: SET_PENDING_TICKET_STATUSES,
    meta: [],
  });
};

export const hasOtherOutcomeSelectedFromTheSameEvent = (pendingBets: PendingBet[], bet: PendingBet) =>
  pendingBets.filter(betItem => betItem.event.id === bet.event.id).length > 0;

export const addPendingBet = (
  betParams: { event: EventItem; offerId: number; outcomeId: number; extraValue?: string },
  intl: IntlShape,
  subscribe = true,
) => {
  const bets = store.getState().bets.pendingBets;

  const uniqueEvents = [...new Set(bets.map(mappedBet => mappedBet.event.id))];
  if (uniqueEvents.length >= MAX_BETS_IN_ACCA && !uniqueEvents.includes(betParams.event.id)) {
    enqueueSnackbarPrompt(intl.formatMessage({ id: 'event.bet_list.maximum_outcome.message' }, { MAX_BETS_IN_ACCA }), {
      variant: SNACKBAR_SEVERITY.WARNING,
    });
    return false;
  }

  const bet: PendingBet = {
    ...betParams,
    added: new Date().getTime(),
    originalOdds: '0',
    originalFractional: '0/0',
    selectedInMultiples: false,
    originalEachWayOdds: '1',
    originalPlaceOdds: 1,
  };

  const sameBetAlreadyExists = (betItem: PendingBet) =>
    betItem.event.id === bet.event.id &&
    betItem.offerId === bet.offerId &&
    betItem.outcomeId === bet.outcomeId &&
    (bet.extraValue ? betItem.extraValue === bet.extraValue : true);
  if (!bets.filter(betItem => sameBetAlreadyExists(betItem)).length) {
    const { oddsString, fractionalOdds, eachWayOdds, placeOdds } = getOfferState(bet);
    setPendingBets([
      ...bets,
      {
        ...bet,
        originalOdds: oddsString,
        originalFractional: fractionalOdds,
        selectedInMultiples: !hasOtherOutcomeSelectedFromTheSameEvent(bets, bet),
        originalPlaceOdds: placeOdds,
        originalEachWayOdds: eachWayOdds,
      },
    ]);

    if (subscribe) {
      getEvent(bet.event.sportId, bet.event.id);
    }
  }
  setBetslipSnackbar(true);
  return true;
};

export const removePendingBet = (bet: { event: EventItem; offerId: number; outcomeId: number; extraValue?: string }) => {
  const {
    bets: { pendingBets },
  } = store.getState();
  const bets = [...pendingBets];

  let betWasSelectedInMultiples = false;
  bets.forEach((betItem, index) => {
    if (
      betItem.event.id === bet.event.id &&
      betItem.offerId === bet.offerId &&
      betItem.outcomeId === bet.outcomeId &&
      (!bet.extraValue || betItem.extraValue === bet.extraValue)
    ) {
      betWasSelectedInMultiples = betItem.selectedInMultiples;

      bets.splice(index, 1);
    }
  });

  if (betWasSelectedInMultiples) {
    for (const pendingBet of bets) {
      if (pendingBet.event.id === bet.event.id) {
        pendingBet.selectedInMultiples = true;
        break;
      }
    }
  }

  const shouldRemoveOutcomeChangesTracker = bets.filter(betItem => betItem.event.id === bet.event.id).length === 0;
  if (shouldRemoveOutcomeChangesTracker) {
    removeOutcomeSelectionChangeTrackerByEventId(String(bet.event.id));
  }

  setPendingBets(bets);
};

export const updateBets = (betsToUpdate: PendingBet[], params: PendingBetParams) => {
  const bets = store.getState().bets.pendingBets;
  const updatedBets = bets.map(betItem => {
    for (const bet of betsToUpdate) {
      if (
        betItem.event.id === bet.event.id &&
        betItem.offerId === bet.offerId &&
        betItem.outcomeId === bet.outcomeId &&
        (!bet.extraValue || betItem.extraValue === bet.extraValue)
      ) {
        return { ...betItem, ...params };
      }
    }
    return betItem;
  });

  setPendingBets(updatedBets);
};

export const removeAllPendingBets = () => {
  setPendingBets([]);
};

export const updatePendingBets = (event: EventItem | undefined) => {
  if (event) {
    const {
      bets: { pendingBets },
    } = store.getState();

    const updatedBets: PendingBet[] = [];
    pendingBets.forEach(pendingBet => {
      if (pendingBet.event.id === event.id) {
        updatedBets.push({ ...pendingBet, event });
      } else {
        updatedBets.push(pendingBet);
      }
    });

    if (!isEqual(pendingBets, updatedBets)) {
      setPendingBets(updatedBets);
    }
  }
};

export const updateOriginalOddsInPendingBets = (bet: PendingBet) => {
  const bets = store.getState().bets.pendingBets;
  const updatedBets = bets.map(betItem => {
    const updatedBet = betItem;
    if (
      betItem.event.id === bet.event.id &&
      betItem.offerId === bet.offerId &&
      betItem.outcomeId === bet.outcomeId &&
      (!bet.extraValue || betItem.extraValue === bet.extraValue)
    ) {
      updatedBet.event = bet.event;
      const { oddsString, fractionalOdds, eachWayOdds, placeOdds } = getOfferState(bet);
      updatedBet.originalOdds = oddsString;
      updatedBet.originalFractional = fractionalOdds;
      updatedBet.originalPlaceOdds = placeOdds;
      updatedBet.originalEachWayOdds = eachWayOdds;
    }
    return updatedBet;
  });

  setPendingBets(updatedBets);
};

export const updateOriginalOddsInPendingAccaBets = (bets: PendingBet[]) => {
  const storeBets = store.getState().bets.pendingBets;
  const updatedBets = storeBets.map(betItem => {
    const updatedBet = betItem;
    bets.forEach(bet => {
      if (
        betItem.event.id === bet.event.id &&
        betItem.offerId === bet.offerId &&
        betItem.outcomeId === bet.outcomeId &&
        (!bet.extraValue || betItem.extraValue === bet.extraValue)
      ) {
        const { oddsString, placeOdds, eachWayOdds } = getOfferState(bet);
        updatedBet.event = bet.event;
        updatedBet.originalOdds = oddsString;
        updatedBet.originalPlaceOdds = placeOdds;
        updatedBet.originalEachWayOdds = eachWayOdds;
      }
    });
    return updatedBet;
  });

  setPendingBets(updatedBets);
};

export const setPlacingBet = (placing: boolean) => {
  store.dispatch({
    type: SET_IS_PLACING_BET,
    meta: placing,
  });
};

export const setBetslipSnackbar = (open: boolean) => {
  store.dispatch({
    type: SET_BETSLIP_SNACKBAR,
    meta: open,
  });
};

export const setTrendingBets = (bets: TrendingBet[]) => {
  store.dispatch({
    type: SET_TRENDING_BETS,
    meta: bets,
  });
};

export const setTrendingBetsCarouselOpen = (meta: boolean) => {
  store.dispatch({
    meta,
    type: SET_TRENDING_BETS_CAROUSEL_OPEN,
  });
};

export const setAutoAcceptOddsChange = (meta: AUTO_ACCEPT_ODDS_CHANGE) => {
  store.dispatch({
    meta,
    type: SET_AUTO_ACCEPT_ODDS_CHANGE,
  });
};

export const enqueueSuperchargedBet = (meta: SuperchargedBetInfo) => {
  store.dispatch({
    meta,
    type: ENQUEUE_SUPERCHARGED_BET,
  });
};

export const dequeueSuperchargedBet = () => {
  store.dispatch({
    type: DEQUEUE_SUPERCHARGED_BET,
  });
};

export const setSuperchargedBets = (meta: SuperchargedBetInfo[]) => {
  store.dispatch({
    meta,
    type: SET_SUPERCHARGED_BETS,
  });
};

export const setAllStakesStatus = (meta: SHOW_TOTAL_STAKES_TOGGLE) => {
  store.dispatch({
    meta,
    type: SET_ALL_STAKES_STATUS,
  });
};

export const setSystemBetConfig = (meta: SystemBetConfigSettings | undefined) => {
  store.dispatch({
    meta,
    type: SET_SYSTEM_BET_CONFIG,
  });
};

export const setSystemBetStakeDetails = (meta: SystemBetStakeDetails) => {
  store.dispatch({
    meta,
    type: SET_SYSTEM_BET_STAKE_DETAILS,
  });
};

export const removeSystemBetStakeDetails = (meta: number) => {
  store.dispatch({
    meta,
    type: REMOVE_SYSTEM_BET_STAKE_DETAILS,
  });
};

export const removeAllSystemBetStakeDetails = () => {
  store.dispatch({
    type: REMOVE_ALL_SYSTEM_BET_STAKE_DETAILS,
  });
};

export const appendSystemBetStakeDetailsArray = (meta: SystemBetStakeDetails[]) => {
  store.dispatch({
    meta,
    type: APPEND_SYSTEM_BET_STAKE_DETAILS_ARRAY,
  });
};

export const setAvailableSystemBetTypes = (meta: SystemBetTypeExtended[]) => {
  store.dispatch({
    meta,
    type: SET_AVAILABLE_SYSTEM_BET_TYPES,
  });
};

export const setSystemBetPlacingOrder = (meta: SystemBetStakeDetails[]) => {
  store.dispatch({
    meta,
    type: SET_SYSTEM_BET_PLACING_ORDER,
  });
};

export const setPendingBets = (meta: PendingBet[]) => {
  store.dispatch({
    meta,
    type: SET_PENDING_BETS,
  });
};

export const setSystemBetStakeValidationStatus = (meta: StringKeyBooleanValuePair) => {
  store.dispatch({
    meta,
    type: SET_SYSTEM_BET_STAKE_VALIDATION_STATUS,
  });
};

export const removeSystemBetStakeValidationStatus = (meta: string) => {
  store.dispatch({
    meta,
    type: REMOVE_SYSTEM_BET_STAKE_VALIDATION_STATUS,
  });
};

export const setOutcomeSelectionByEventChanged = (meta: OutcomeSelectionByEventChanged) => {
  store.dispatch({
    meta,
    type: SET_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  });
};

export const removeOutcomeSelectionChangeTrackerByEventId = (meta: string) => {
  store.dispatch({
    meta,
    type: REMOVE_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  });
};

export const removeAllOutcomeSelectionChangeTracker = () => {
  store.dispatch({
    type: REMOVE_ALL_OUTCOME_SELECTION_BY_EVENT_CHANGES,
  });
};
